/**
 * WHICH NYUMBASUITE AM I LOOKING AT?
 *
 * Demo and live are the same build, the same brand and the same login screen,
 * on two hostnames. That is the point of a demo — and it is also how a landlord
 * spent nine minutes failing to sign in to demo with her live password, asked
 * for three password resets that could never arrive, and successfully reset a
 * DIFFERENT account's password before thinking to change the address bar.
 * Every request in that session was answered correctly. Nothing on the screen
 * told her where she was.
 *
 * The authenticator entries already solved this once — a code from demo is
 * labelled "NyumbaSuite (demo)" so two identical entries can be told apart.
 * This is the same fix for the screen the user is actually looking at.
 *
 * Read from the hostname rather than from the build or the API on purpose:
 *   · a build-time flag is baked into a bundle that both deployments serve;
 *   · /api/v1/version reports NODE_ENV, which is "production" on BOTH, so it
 *     cannot answer this question at all;
 *   · the hostname is what the browser is actually talking to, needs no
 *     request, and cannot be stale.
 */

/** Everything that is not the real thing, and what to call it. */
const NOT_PRODUCTION: Array<[RegExp, string]> = [
  [/^demo\./i, 'Demo'],
  [/^staging\./i, 'Staging'],
  [/^test\./i, 'Test'],
  [/^(localhost|127\.0\.0\.1|\[::1\])$/i, 'Local'],
  // A dev server on a LAN address, which is somebody testing on their phone.
  [/^(10\.|192\.168\.|172\.(1[6-9]|2[0-9]|3[01])\.)/, 'Local'],
];

/**
 * The name of this deployment, or null when it is the live one.
 *
 * Null rather than 'Production' because the live site must carry NO badge:
 * a marker that appears everywhere is furniture, and furniture is not read.
 * It earns attention by being unusual.
 */
export function deploymentLabel(hostname: string): string | null {
  const h = hostname.trim();
  for (const [pattern, label] of NOT_PRODUCTION) {
    if (pattern.test(h)) return label;
  }
  return null;
}

/** The same answer for the browser we are running in. */
export function currentDeployment(): string | null {
  if (typeof window === 'undefined') return null;
  return deploymentLabel(window.location.hostname);
}

/**
 * Put it in the tab title too.
 *
 * The badge is on the page; the title is on the tab, which is what somebody
 * sees when demo and live are open side by side — which is exactly the
 * situation that produces the confusion.
 */
export function markTitle(): void {
  const label = currentDeployment();
  if (!label || typeof document === 'undefined') return;
  const prefix = `${label.toUpperCase()} — `;
  if (!document.title.startsWith(prefix)) document.title = prefix + document.title;
}
